import * as React from "react";
import { deleteSession, getCurrentUser, User } from "../utils/api";

export type AuthStatus = "loading" | "authenticated" | "anonymous";

export type AuthContextValue = {
  user: User | null;
  status: AuthStatus;
  signOut: () => Promise<void>;
  refresh: () => Promise<User | null>;
};

// A real default (rather than throwing) keeps SSR/prerender of any consumer
// safe: pages render their "loading" branch until the provider hydrates.
const AuthContext = React.createContext<AuthContextValue>({
  user: null,
  status: "loading",
  signOut: async () => {},
  refresh: async () => null,
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<User | null>(null);
  const [status, setStatus] = React.useState<AuthStatus>("loading");

  const load = React.useCallback(
    async (force: boolean): Promise<User | null> => {
      const current = await getCurrentUser({ force }).catch(() => null);
      setUser(current);
      setStatus(current ? "authenticated" : "anonymous");
      return current;
    },
    []
  );

  // Callers reach for refresh() after changing something (activation, a plan
  // change), so it always goes to the network; only the mount load below is
  // allowed to share a de-duplicated result.
  const refresh = React.useCallback(() => load(true), [load]);

  const signOut = React.useCallback(async () => {
    await deleteSession();
    setUser(null);
    setStatus("anonymous");
  }, []);

  React.useEffect(() => {
    // One-time cleanup of the legacy localStorage pseudo-auth.
    localStorage.removeItem("verifiedEmail");
    localStorage.removeItem("apiKey");
    load(false);
  }, [load]);

  const value = React.useMemo(
    () => ({ user, status, signOut, refresh }),
    [user, status, signOut, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => React.useContext(AuthContext);
